import React from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import './MessageDialog.scss'

/**
 * MessageDialog — shared replacement for the legacy `'generic'` modal type
 * handled inline in modal/Modal.jsx. All current callers pass only
 * { title, description } with no rich content, per-caller buttons, or
 * callbacks, so this single component covers every case. Do not add a
 * severity/variant prop until a caller actually needs to distinguish
 * success vs. error presentation — today they share identical markup.
 *
 * Dialog wrapper convention for this refactor (BR-3138): MUI's Dialog
 * supplies the backdrop, backdrop-click close, Escape-to-close, focus trap,
 * and portalling that the old Modal.jsx hand-rolled via
 * .modal-elements > .modal-full-screen-overlay > .modal > .modal-container
 * (see Modal.jsx:69-75, :45-53) — none of that is reimplemented here.
 * Each converted modal keeps its own existing inner markup and SCSS class
 * names inside <DialogContent>, so the per-modal SCSS keeps applying.
 * See also LandingPageModal.jsx (same pattern) and ForgotPasswordModal.jsx
 * (open/handleClose prop convention).
 *
 * Unlike the old 'generic' modal — which was listed in `needsExternalClose`
 * but whose close button was commented out (Modal.jsx:131-142), leaving it
 * with no visible close affordance — MessageDialog gets a real close
 * button. That's an intended improvement, not a regression.
 */
export function MessageDialog({ open, handleClose, title, description }) {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="message-dialog-title"
      aria-describedby="message-dialog-description"
    >
      <DialogContent>
        <div className="modal-generic">
          <div className="modal-header">
            <span className="modal-header-generic" id="message-dialog-title">
              {title}
            </span>
          </div>
          <div className="content-body" id="message-dialog-description">
            <p>{description}</p>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} autoFocus>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
